import dayjs from "dayjs";
import {
    ApplicationTypes,
    AssetTypes, BoolLike,
    BuildType,
    DebtTypes,
    EmploymentTypes,
    Frequencies,
    MortgageTypes,
    PersonalAccommodationSituations
} from "../../../../../../../models";
import {legacyIncome} from "../useXlsTests";
import {bankStatementSummary} from "../../../../_new/savings-rate/BankingSavingsRate";

export function extrapolateYearlyAmount(amounts, frequency) {
    if (!amounts.length) {
        return null
    }
    let average = amounts.reduce((a, b) => a + b, 0) / amounts.length
    switch (frequency) {
        case 'WEEKLY':
            return average * (365 / 7)
        case 'FORTNIGHTLY':
            return average * (365 / 14)
        case 'MONTHLY':
            return average * 12
        case 'YEARLY':
            return average
        case "QUARTERLY" :
            return average * 4
    }
}


export class figuresSource {
    constructor() {
        this.errors = []
        this.warnings = []
        this.value = null
        this.methodogy = new methodology()
    }

    addError(error) {
        if (typeof error === 'string') {
            this.errors.push(error)
        } else if (error instanceof Error) {
            this.errors.push(error.message)
        } else {
            this.errors.push(JSON.stringify(error))
        }
    }

    addWarning(warning) {
        if (typeof warning === 'string') {
            this.warnings.push(warning)
        } else if (warning instanceof Error) {
            this.warnings.push(warning.message)
        } else {
            this.warnings.push(JSON.stringify(warning))
        }
    }

    setValue(value) {
        this.value = value
    }

    getValue(verified = false) {
        return this.value
    }

}

class methodology {
    constructor() {
        this.rows = []
    }

    add(label, value, operator = '=') {
        this.rows.push({
            type: 'ROW',
            label,
            value,
            operator
        })
    }

    total(label, value) {
        this.rows.push({
            type: 'TOTAL',
            label,
            value
        })
    }

}

export function mortgageToExpandedFigures(mortgage, verified = false, stage = 'APPLY') {

    const {currencyAccounts} = bankStatementSummary(mortgage)
    const today = dayjs()
    const legacyEmployment = legacyIncome(mortgage)

    const allDependentAges = () => {
        // RETURNS ARRAY OF OBJECTS WITH AGES [{age:5}, {age:6}]
        const source = new figuresSource()

        const processDependents = (applicantN) => {
            const appCorrect = []
            const appWonky = []
            const app = mortgage[`applicant${applicantN}`].personal
            if (app?.countDependents > 0) {
                const appDependents = app.dependentDOBs || []
                for (const dep of appDependents) {
                    let dob = dayjs(dep, "YYYY-MM-DD", true)
                    if (dob?.isValid()) {
                        let age = today.diff(dob, 'year', true)
                        if (age < 0) {
                            source.addError(`Applicant ${applicantN} Dependent DOB ${dep} is in the future`)
                            age = 0
                        }
                        appCorrect.push({age: Math.round(age)})
                    } else {
                        appWonky.push(dep)
                    }
                }
                if (appWonky.length) {
                    source.addWarning(`Applicant ${applicantN} has ${appWonky} invalid dependent DOB`)
                }
            }

            return appCorrect
        }
        const dependents = processDependents(1)
        if (mortgage.twoApplicants) {
            dependents.push(...processDependents(2))
        }
        source.setValue(dependents)
        return source
    }
    const fundsAvailable = () => {
        const source = new figuresSource()
        let sum = 0
        if (verified) {
            if (stage === 'PREAPPROVAL') {
                const confirmedSavingsSection = mortgage.uploadSections.find(it => it.name === 'proof-of-deposit' && it.stage === 'PREAPPROVAL' && it.isActive)
                const confirmedSavings = confirmedSavingsSection ? confirmedSavingsSection.activeUploads.reduce(
                    (sum, upload) => sum + (upload.values?.closingBalance || 0), 0
                ) : 0
                sum += confirmedSavings || 0
                source.methodogy.add('Sum of proof of deposit statements', confirmedSavings)
            } else {
                if (currencyAccounts.hasOwnProperty('€')) {
                    // get the available funds from the wealth table
                    const {accounts, totalBalance} = currencyAccounts['€']
                    accounts.forEach((account, i) => {

                        source.methodogy.add(account.personalTitle, account?.closingOnDate?.balance || 0, i === accounts.length - 1 ? '+' : null)
                    })
                    source.methodogy.total('Total', totalBalance || 0)
                    sum += totalBalance || 0
                }

            }


            if (mortgage.requirement.receivingGifts) {
                // the amount from the all gift letters in the section
                const section = mortgage.uploadSections.find(s => s.name === 'gift-certificate' && s.isActive)
                if (section) {

                    section.uploads.filter(u => u.isActive && u.isVerifiable)
                        .forEach((upload, i) => {
                            if (!upload.values.amount) {
                                source.addWarning(`Gift certificate found but no amount provided`)
                            } else {
                                let value = Number(upload.values.amount)
                                sum += value
                                source.methodogy.add(`Gift from ${upload.values.gifterNames}`, value, i === upload.length - 1 ? '+' : null)
                            }
                        })
                } else {
                    source.addWarning(`Receiving gifts but gift certificate section disabled`)
                }


            }


        } else {
            // Add gifts
            if (mortgage.requirement.receivingGifts) {
                if (!mortgage.requirement.depositGifts) {
                    source.addError(`Receiving gifts but no gifts amount provided`)
                }
                let gifts = mortgage.requirement.depositGifts || 0
                sum += gifts
                source.methodogy.add('Gifts', gifts)
            }

            // Add Savings
            if (!mortgage.requirement.depositSavings) {
                source.addWarning(`No deposit savings amount provided`)
            }
            let savings = mortgage.requirement.depositSavings || 0
            sum += savings
            source.methodogy.add('Own Savings', savings, mortgage.requirement.receivingGifts ? '+' : null)

        }
        source.setValue(sum)
        source.methodogy.total('Total', sum)
        return source
    }
    const applicant = (n) => {
        if (n === 2 && !mortgage.twoApplicants) {
            return null
        }
        const app = mortgage[`applicant${n}`]
        const age = () => {
            const source = new figuresSource()
            const dob = dayjs(app.personal.dateOfBirth, "YYYY-MM-DD", true)
            let age = 0
            if (!dob?.isValid()) {
                source.addError(`Applicant ${n} DOB is not a valid date`)
            } else {
                age = today.diff(dob, 'year', true)
                if (age < 0) {
                    source.addError(`Applicant ${n} DOB is in the future`)
                    age = 0
                }
            }
            source.setValue(Math.round(age))

            return source
        }
        const maritalStatus = () => {
            const source = new figuresSource()
            if (!app.personal.maritalStatus) {
                source.addWarning(`Applicant ${n} marital status not provided`)
            }
            source.setValue(app.personal.maritalStatus || 'SINGLE')
            return source
        }
        const works = () => {
            const source = new figuresSource()
            const works = [
                EmploymentTypes.EMPLOYED,
                EmploymentTypes.SELFEMPLOYED
            ]

            if (legacyEmployment) {
                if (!app.employment?.employmentType) {
                    source.addError(`Applicant ${n} employment type not provided`)
                }
                source.setValue(works.includes(applicant?.employment?.employmentType || false))
            } else {
                if (app.employmentIncome.some(income => !income.employmentType)) {
                    source.addError(`Applicant ${n} employment type not provided`)
                }
                source.setValue(!!app.employmentIncome.find(income => works.includes(income.employmentType)))
            }
            return source
        }
        const pra_savings = () => {
            const source = new figuresSource()
            let savings = 0
            if (verified) {
                if (currencyAccounts.hasOwnProperty('€')) {
                    const {accounts} = currencyAccounts['€']

                    const applicableAccounts = accounts.filter(a => a.applicant === n && a.includeInCalculations)
                    applicableAccounts.forEach((account, i) => {
                        source.methodogy.add(account.personalTitle, account.averageSavings || 0, i === accounts.length - 1 ? '+' : null)
                        savings += account.averageSavings || 0
                    })
                }


            } else {
                if ((app.financial.hasSavings || app.financial.hasCurrentAccountSavings) && app.financial.monthlySavings) {
                    if (!app.financial.monthlySavings) {
                        if (app.financial.hasSavings) {
                            source.addWarning(`Applicant ${n} has savings but savings amount not provided`)
                        }
                        if (app.financial.hasCurrentAccountSavings) {
                            source.addWarning(`Applicant ${n} has current account savings but savings amount not provided`)
                        }
                    }
                    source.methodogy.add(`Savings Rate`, app.financial.monthlySavings || 0)
                    savings = app.financial.monthlySavings || 0
                } else {
                    let filter = asset => asset.assetType === AssetTypes.SAVING && [
                        Frequencies.MONTHLY,
                        Frequencies.WEEKLY,
                        Frequencies.FORTNIGHTLY,
                    ].includes(asset.increasesFrequency)
                    let allSavings = app.assets.filter(filter).map(asset => {
                        let acc = 0
                        switch (asset.increasesFrequency) {
                            case Frequencies.MONTHLY:
                                acc += asset.increases
                                break
                            case Frequencies.FORTNIGHTLY:
                                acc += Number((asset.increases * 26 / 12).toFixed(2))
                                break
                            default:
                                acc += Number((asset.increases * 52 / 12).toFixed(2))
                        }
                        return {
                            ...asset,
                            monthlyIncrease: acc
                        }
                    })
                    allSavings.forEach((account, i) => {
                        source.methodogy.add(`${app.firstName} ${account.institution} (${account.increases} / ${account.increasesFrequency})`, account.monthlyIncrease, i === allSavings.length - 1 ? '+' : null)
                        savings += account.monthlyIncrease
                    })
                }
            }
            source.methodogy.total('Total', savings)
            source.setValue(savings)
            return source
        }
        const pra_rent = () => {
            const source = new figuresSource()

            // if cohabiting and applicant1 is renting - get the amount app2 contibutes to rent
            const rentingWithOther = n === 2 && mortgage.requirement.cohabiting && mortgage.applicant1.personal.accommodationSituation === PersonalAccommodationSituations.RENTED
            if (rentingWithOther) {
                if (app.financial.monthlyAccommodationCosts == null) {
                    source.addWarning(`Applicant ${n} rent costs not provided`)
                } else {
                    source.methodogy.add(`Rent from Applicant ${n}`, app.financial.monthlyAccommodationCosts)
                    source.setValue(app.financial.monthlyAccommodationCosts)
                }
                return source
            } else {
                if (app.personal.accommodationSituation === PersonalAccommodationSituations.RENTED && (!mortgage.requirement.cohabiting || n === 1)) {
                    if (app.financial.monthlyAccommodationCosts == null) {
                        source.addWarning(`Applicant ${n} rent costs not provided`)
                    } else {
                        source.methodogy.add(`Rent from Applicant ${n}`, app.financial.monthlyAccommodationCosts)
                        source.setValue(app.financial.monthlyAccommodationCosts)
                    }
                }
                return source
            }
        }
        const pra_boarding = () => {
            const source = new figuresSource()
            const boardingWithOther = n === 2 && mortgage.requirement.cohabiting && mortgage.applicant1.personal.accommodationSituation === PersonalAccommodationSituations.RELATIVES
            if (boardingWithOther) {
                if (app.financial.monthlyAccommodationCosts == null && app.financial.contributeToRelatives) {
                    source.addWarning(`Applicant ${n} boarding costs not provided`)
                } else {
                    source.methodogy.add(`Boarding from Applicant ${n}`, app.financial.monthlyAccommodationCosts)
                    source.setValue(app.financial.monthlyAccommodationCosts || 0)
                }
                return source
            } else {
                if (app.personal.accommodationSituation === PersonalAccommodationSituations.RELATIVES && app.financial.contributeToRelatives) {
                    if (app.financial.monthlyAccommodationCosts == null) {
                        source.addWarning(`Applicant ${n} boarding costs not provided`)
                    } else {
                        source.methodogy.add(`Boarding from Applicant ${n}`, app.financial.monthlyAccommodationCosts)
                        source.setValue(app.financial.monthlyAccommodationCosts || 0)
                    }
                }
                return source
            }
        }
        const pra_mortgage = () => {
            const mortgagingWithOther = n === 2 && mortgage.requirement.cohabiting && mortgage.applicant1.personal.accommodationSituation === PersonalAccommodationSituations.OWNED
            if (app.personal.accommodationSituation === PersonalAccommodationSituations.OWNED || mortgagingWithOther) {
                const source = new figuresSource()
                let mortgageAmt = app.financial.monthlyAccommodationCosts || 0
                if (mortgageAmt) {
                    source.methodogy.add(`Mortgage Repayments from Applicant ${n}`, mortgageAmt)
                } else {
                    source.addWarning(`Applicant ${n} mortgage costs not provided`)
                }
                source.setValue(mortgageAmt)
                return source
            }
        }
        const pra_clearingLoans = () => {
            const source = new figuresSource()
            let clearingLoans = app.debts.filter(d => d.debtType === DebtTypes.LOAN && !!d.clearedBeforeMortgage && !d.isClosed)
            if (clearingLoans.length) {
                let sum = 0
                clearingLoans.forEach((debt, i) => {
                    if (!debt.monthlyPayment) {
                        source.addWarning(`Applicant ${n} clearing loan ${debt.name} monthly payment not provided`)
                    }
                    source.methodogy.add(`Clearing Loan ${debt.name} from Applicant ${n}`, debt.monthlyPayment || 0, i === clearingLoans.length - 1 ? '+' : null)
                    sum += debt.monthlyPayment || 0
                })
                source.methodogy.total(`Total Clearing Loans from Applicant ${n}`, sum)
                source.setValue(sum)
                return source
            }

        }
        const outgoings_keepingLoans = () => {
            const source = new figuresSource()
            const loans = app.debts.filter(d => d.debtType === DebtTypes.LOAN && !d.clearedBeforeMortgage && !d.isClosed)
            if (loans.length) {
                let sum = 0
                loans.forEach((debt, i) => {
                    if (!debt.monthlyPayment) {
                        source.addWarning(`Applicant ${n} loan ${debt.lender} monthly payment not provided`)
                    }
                    source.methodogy.add(`Loan ${debt.lender} from Applicant ${n}`, debt.monthlyPayment || 0, i === loans.length - 1 ? '+' : null)
                    sum += debt.monthlyPayment || 0
                })
                source.methodogy.total(`Total Loans from Applicant ${n}`, sum)
                source.setValue(sum)
            } else {
                source.setValue(0)
            }
            return source
        }
        const outgoings_maintenance = () => {
            const source = new figuresSource()
            if (app.financial.paysMaintenance) {
                if (app.financial.maintenanceCosts == null) {
                    source.addWarning(`Applicant ${n} maintenance costs not provided`)
                } else {
                    source.methodogy.add(`Maintenance Costs for Applicant ${n}`, app.financial.maintenanceCosts)
                    source.setValue(app.financial.maintenanceCosts)
                }
            }
            return source
        }
        const outgoings_childcare = () => {
            const source = new figuresSource()
            if (app.financial.hasChildcareCosts) {
                if (app.financial.monthlyChildcareCosts == null) {
                    source.addWarning(`Applicant ${n} childcare costs not provided`)
                } else {
                    source.methodogy.add(`Childcare Costs for Applicant ${n}`, app.financial.monthlyChildcareCosts)
                    source.setValue(app.financial.monthlyChildcareCosts)
                }
            }
            return source

        }
        const incoming_childrens_allowance = () => {
            const source = new figuresSource()
            source.methodogy.add(`Children's Allowance for Applicant ${n}`, app.financial.monthlyChildrenAllowance || 0)
            source.setValue(app.financial.monthlyChildrenAllowance || 0)
            return source
        }
        const incoming_other_allowance = () => {
            const source = new figuresSource()
            source.methodogy.add(`Other Allowance for Applicant ${n}`, app.financial.monthlyOtherAllowance || 0)
            source.setValue(app.financial.monthlyOtherAllowance || 0)
            return source
        }

        const _credit = () => {
            let sumLatestCreditLimits = 0
            let sumHighestCreditUtilization = 0
            const figureLatestCreditLimits = new figuresSource()
            const figureHighestCreditUtilization = new figuresSource()


            const credits = app.debts.filter(debt => [
                DebtTypes.CREDITCARD,
                DebtTypes.STORECARD,
            ].includes(debt.debtType) && !debt.isClosed)

            credits.forEach((debt, i) => {
                if (verified) {
                    // most recent credit limit
                    const section = mortgage.uploadSections.find(s => s.stage === 'APPLY' && s.name === `debts-${n}` && s.isActive && s.index === debt.id)
                    if (section){
                        const uploads = (section.activeUploads ||[]).filter(it=>it.values?.documentDate)
                        const mostRecentUpload = uploads.reduce((latest, upload) => {

                            const uploadDate = upload.values.documentDate
                            if (!latest || uploadDate.isAfter(latest.values.documentDate)) {
                                return upload
                            }
                            return latest
                        }, null)
                        if (mostRecentUpload){
                            figureLatestCreditLimits.methodogy.add(`Credit Limit from most recent ${debt.lender} upload`, mostRecentUpload.values.creditLimit, i === uploads.length - 1 ? '+' : null)
                            sumLatestCreditLimits += mostRecentUpload.values.creditLimit || 0
                        }
                        else{
                            figureLatestCreditLimits.addWarning(`No uploads found for ${debt.lender} to verify credit limit`)
                        }

                        // highest credit utilization
                        const highestOutstandingBalance = uploads.reduce((highest, upload) => {
                            const balance = upload.values.outstandingBalance || 0
                            if (balance > highest) {
                                return balance
                            }
                            return highest
                        }, 0)
                        if (highestOutstandingBalance) {
                            figureHighestCreditUtilization.methodogy.add(`Highest Outstanding Balance from ${debt.lender} uploads`, highestOutstandingBalance, i === uploads.length - 1 ? '+' : null)
                            sumHighestCreditUtilization += highestOutstandingBalance
                        }
                        else{
                            figureHighestCreditUtilization.addWarning(`No uploads found for ${debt.lender} to verify highest credit utilisation`)
                        }
                    }

                } else {
                    if (!debt.creditLimit) {
                        figureLatestCreditLimits.addWarning(`Applicant ${n} credit limit for ${debt.name} not provided`)
                    }
                    figureLatestCreditLimits.methodogy.add(`Credit Limit ${debt.lender} from Applicant ${n}`, debt.creditLimit || 0, i === credits.length - 1 ? '+' : null)
                    sumLatestCreditLimits += debt.creditLimit || 0
                    if (!debt.highestCreditUtilisation) {
                        figureHighestCreditUtilization.addWarning(`Applicant ${n} highest credit utilisation for ${debt.name} not provided`)
                    }
                    figureHighestCreditUtilization.methodogy.add(`Highest Credit Utilisation ${debt.lender} from Applicant ${n}`, debt.highestCreditUtilisation || 0, i === credits.length - 1 ? '+' : null)
                    sumHighestCreditUtilization += debt.highestCreditUtilisation || 0
                }
            })

            figureLatestCreditLimits.methodogy.total(`Total Credit Limits from Applicant ${n}`, sumLatestCreditLimits)
            figureLatestCreditLimits.setValue(sumLatestCreditLimits)
            figureHighestCreditUtilization.methodogy.total(`Total Highest Credit Utilisation from Applicant ${n}`, sumHighestCreditUtilization)
            figureHighestCreditUtilization.setValue(sumHighestCreditUtilization)
            return {
                latestCreditLimits: figureLatestCreditLimits,
                highestCreditUtilization: figureHighestCreditUtilization
            }
        }
        const _overdraft = () => {
            let sumLatestOverdraftLimits = 0
            let sumHighestOverdraftUtilization = 0
            const figureLatestOverdraftLimits = new figuresSource()
            const figureHighestOverdraftUtilization = new figuresSource()
            let caOverdrafts = app.accounts.filter(it => it.hasOverdraft)
            /// for now, overdrafts are saved in the current account table
            caOverdrafts.forEach((account, i) => {
                // if (verified) {
                //     const section = mortgage.uploadSections.find(s => s.stage === 'APPLY' && s.name === `current-accounts-${n}` && s.isActive && s.index === account.id)
                //     if (section) {
                //         const uploads = (section.activeUploads ||[]) .filter(it => it.values?.endDate)
                //         console.log({uploads})
                //         const mostRecentUpload = uploads.reduce((latest, upload) => {
                //             const uploadDate = upload.values.endDate
                //             if (!latest || uploadDate.isAfter(latest.values.endDate)) {
                //                 return upload
                //             }
                //             return latest
                //         }, null)
                //         figureLatestOverdraftLimits.methodogy.add(`Overdraft Limit from most recent ${account.institution} upload`, mostRecentUpload.values.overdraftLimit, i === uploads.length - 1 ? '+' : null)
                //         sumLatestOverdraftLimits += mostRecentUpload.values.overdraftLimit || 0
                //         // highest overdraft utilization
                //         const highestOutstandingBalance = uploads.reduce((highest, upload) => {
                //             const balance = upload.values.outstandingBalance || 0
                //             if (balance > highest) {
                //                 return balance
                //             }
                //             return highest
                //         }, 0)
                //         figureHighestOverdraftUtilization.methodogy.add(`Highest Outstanding Balance from ${account.institution} uploads`, highestOutstandingBalance, i === uploads.length - 1 ? '+' : null)
                //         sumHighestOverdraftUtilization += highestOutstandingBalance
                //     }
                // } else {
                    if (!account.overdraftLimit) {
                        figureLatestOverdraftLimits.addWarning(`Applicant ${n} overdraft limit for ${account.institution} not provided`)
                    }
                    figureLatestOverdraftLimits.methodogy.add(`Overdraft Limit ${account.institution} from Applicant ${n}`, account.overdraftLimit || 0, i === caOverdrafts.length - 1 ? '+' : null)
                    sumLatestOverdraftLimits += account.overdraftLimit || 0
                    if (!account.highestCreditUtilisation) {
                        figureHighestOverdraftUtilization.addWarning(`Applicant ${n} highest overdraft utilisation for ${account.institution} not provided`)
                    }
                    figureHighestOverdraftUtilization.methodogy.add(`Highest Overdraft Utilisation ${account.institution} from Applicant ${n}`, account.highestOverdraftUtilisation || 0, i === caOverdrafts.length - 1 ? '+' : null)
                    sumHighestOverdraftUtilization += account.highestCreditUtilisation || 0
                // }
            })
            figureLatestOverdraftLimits.methodogy.total(`Total Overdraft Limits from Applicant ${n}`, sumLatestOverdraftLimits)
            figureLatestOverdraftLimits.setValue(sumLatestOverdraftLimits)
            figureHighestOverdraftUtilization.methodogy.total(`Total Highest Overdraft Utilisation from Applicant ${n}`, sumHighestOverdraftUtilization)
            figureHighestOverdraftUtilization.setValue(sumHighestOverdraftUtilization)
            return {
                latestOverdraftLimits: figureLatestOverdraftLimits,
                highestOverdraftUtilization: figureHighestOverdraftUtilization
            }

        }

        const creditFacilityLimits = () => {
            // CREDIT CARDS
            const credits = app.debts.filter(debt => [
                DebtTypes.CREDITCARD,
                DebtTypes.STORECARD,
            ].includes(debt.debtType) && !debt.isClosed)
            const source = new figuresSource()
            let sum = 0
            if (credits.length) {


                credits.forEach((debt, i) => {
                    if (verified) {
                        const section = mortgage.uploadSections.find(s => s.stage === 'APPLY' && s.name === `debts-${n}` && s.isActive && s.index === debt.id)
                        if (!section) {
                            source.addWarning(`No credit card section found for applicant ${n}`)
                        }


                        const yearNeeded = Number(dayjs().year() - 1)

                        const eds = section?.uploads?.find(u => u.isActive && u.isVerifiable && Number(u.year) === yearNeeded)
                        if (!eds) {
                            source.addWarning(`No employment summary found for applicant ${n} for year ${yearNeeded}`)
                        } else if (!eds.values || !eds.values.pay1) {
                            source.addWarning(`Employment summary for applicant ${n} for year ${yearNeeded} does not have pay1 value`)
                        } else {

                            source.methodogy.add(`Employment Summary Pay1 for Applicant ${n} for year ${yearNeeded}`, eds.values.pay1)
                            source.setValue(eds.values.pay1)
                        }
                    } else {
                        if (!debt.creditLimit) {
                            source.addWarning(`Applicant ${n} credit limit for ${debt.name} not provided`)
                        }
                        source.methodogy.add(`Credit Limit ${debt.lender} from Applicant ${n}`, debt.creditLimit || 0, i === credits.length - 1 ? '+' : null)
                        sum += debt.creditLimit || 0
                    }
                })
                source.methodogy.total(`Total Credit Limits from Applicant ${n}`, sum)

            }
            let caOverdrafts = app.accounts.filter(it => it.hasOverdraft)
            if (caOverdrafts.length) {
                caOverdrafts.forEach((account, i) => {
                    if (!account.overdraftLimit) {
                        source.addWarning(`Applicant ${n} overdraft limit for ${account.institution} not provided`)
                    }
                    source.methodogy.add(`Overdraft Limit ${account.institution} from Applicant ${n}`, account.overdraftLimit || 0, i === caOverdrafts.length - 1 ? '+' : null)
                    sum += account.overdraftLimit || 0
                })
                source.methodogy.total(`Total Overdraft Limits from Applicant ${n}`, sum)
            }
            source.setValue(sum)
            return source
        }

        const lastP60 = () => {
            const source = new figuresSource()
            if (verified) {
                const section = mortgage.uploadSections.find(s => s.stage === 'APPLY' && s.name === `employment-summary-${n}` && s.isActive)
                if (!section) {
                    source.addWarning(`No employment summary section found for applicant ${n}`)
                }
                const yearNeeded = Number(dayjs().year() - 1)

                const eds = section?.uploads?.find(u => u.isActive && u.isVerifiable && Number(u.year) === yearNeeded)
                if (!eds) {
                    source.addWarning(`No employment summary found for applicant ${n} for year ${yearNeeded}`)
                } else if (!eds.values || !eds.values.pay1) {
                    source.addWarning(`Employment summary for applicant ${n} for year ${yearNeeded} does not have pay1 value`)
                } else {

                    source.methodogy.add(`Employment Summary Pay1 for Applicant ${n} for year ${yearNeeded}`, eds.values.pay1)
                    source.setValue(eds.values.pay1)
                }
            }
            return source
        }
        const jobs = () => {
            // RETURNS AN ARRAY OF JOB OBJECTS eg. {sector:'PRIVATE', basic:{yearMostRecent: 50000, guaranteed:true}, shiftAllowance:{yearMostRecent: 5000, yearBefore: 4000, yearBeforeThat: 3000, guaranteed:true}, ...}
            let jobs = []
            if (legacyEmployment) {
                jobs.push({
                    employmentType: app.employment.employmentType,
                    payFrequency: app.income.payFrequency,
                    grossBasic: app.employment.grossBasic,

                })
            } else {
                jobs = app.employmentIncome
            }
            if (!works()) {
                return []
            }

            return jobs.map(emp => {
                const source = new figuresSource()
                let obj = {
                    sector: emp.employmentType === EmploymentTypes.SELFEMPLOYED ? 'SELF' : emp.publicSector ? 'PUBLIC' : 'PRIVATE',
                    basic: {
                        yearMostRecent: 0,
                        guaranteed: true
                    },
                }
                if (emp.publicSector) {
                    obj.payScale = [emp.nextPayScale1 || '', emp.nextPayScale2 || '', emp.nextPayScale3 || '']
                }

                if (verified) {

                    const section = mortgage.uploadSections.find(s => s.stage === 'APPLY' && s.name === `salary-cert-${n}` && s.isActive && (legacyEmployment || s.index === emp.id))

                    if (!section) {
                        source.addWarning(`No salary cert section found for applicant ${n} job ${emp.id || 'unknown'}`)
                        obj.basic.yearMostRecent = emp.grossBasic || 0;
                        (emp.incomes || []).filter(it => !!it).forEach(income => {
                            if (income.yearMostRecent || income.amount || income.yearBefore || income.yearBeforeThat) {
                                switch (income.name) {
                                    case 'bonus':

                                        obj.bonus = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }

                                        break
                                    case 'overtime':
                                        obj.overtime = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'commission':
                                        obj.commission = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'shiftAllowance':
                                        obj.shiftAllowance = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'otherAllowance':
                                        obj.otherAllowance = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'carAllowance':
                                        obj.carAllowance = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                }
                            }
                        })
                    } else {
                        (emp.incomes || []).filter(it => !!it).forEach(income => {
                            if (income.yearMostRecent || income.amount || income.yearBefore || income.yearBeforeThat) {
                                switch (income.name) {
                                    case 'bonus':

                                        obj.bonus = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }

                                        break
                                    case 'overtime':
                                        obj.overtime = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'commission':
                                        obj.commission = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'shiftAllowance':
                                        obj.shiftAllowance = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'otherAllowance':
                                        obj.otherAllowance = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'carAllowance':
                                        obj.carAllowance = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                }
                            }
                        })
                        const doc = section.uploads.find(u => u.isActive && u.isVerifiable && u.values && u.values.grossBasicThisYear)

                        if (!doc) {

                            source.addWarning(`No active employment summary found for applicant ${n} job ${emp.id || ''}`)
                            obj.basic.yearMostRecent = emp.grossBasic || 0;
                        } else {
                            obj.basic.yearMostRecent = doc.values.grossBasicThisYear || 0
                            if (doc.values.shiftAllowanceYearMostRecent || doc.values.shiftAllowanceYearBefore || doc.values.shiftAllowanceYearBeforeThat) {
                                obj.shiftAllowance = {
                                    yearMostRecent: doc.values.shiftAllowanceYearMostRecent || 0,
                                    yearBefore: doc.values.shiftAllowanceYearBefore || 0,
                                    yearBeforeThat: doc.values.shiftAllowanceYearBeforeThat || 0,
                                    guaranteed: doc.values.shiftAllowanceGuaranteed || doc.values.allowancesGuaranteed || false
                                }
                            }
                            if (doc.values.carAllowanceYearMostRecent || doc.values.carAllowanceYearBefore || doc.values.carAllowanceYearBeforeThat) {
                                obj.carAllowance = {
                                    yearMostRecent: doc.values.carAllowanceYearMostRecent || 0,
                                    yearBefore: doc.values.carAllowanceYearBefore || 0,
                                    yearBeforeThat: doc.values.carAllowanceYearBeforeThat || 0,
                                    guaranteed: doc.values.carAllowanceGuaranteed || false
                                }
                            }
                            if (doc.values.otherAllowanceYearMostRecent || doc.values.otherAllowanceYearBefore || doc.values.otherAllowanceYearBeforeThat) {
                                obj.otherAllowance = {
                                    yearMostRecent: doc.values.otherAllowanceYearMostRecent || 0,
                                    yearBefore: doc.values.otherAllowanceYearBefore || 0,
                                    yearBeforeThat: doc.values.otherAllowanceYearBeforeThat || 0,
                                    guaranteed: doc.values.otherAllowanceGuaranteed || false
                                }
                            }
                            if (doc.values.overtimeYearMostRecent || doc.values.overtimeYearBefore || doc.values.overtimeYearBeforeThat) {
                                obj.overtime = {
                                    yearMostRecent: doc.values.overtimeYearMostRecent || 0,
                                    yearBefore: doc.values.overtimeYearBefore || 0,
                                    yearBeforeThat: doc.values.overtimeYearBeforeThat || 0,
                                    guaranteed: doc.values.overtimeGuaranteed || false
                                }
                            }
                            if (doc.values.bonusYearMostRecent || doc.values.bonusYearBefore || doc.values.bonusYearBeforeThat) {
                                obj.bonus = {
                                    yearMostRecent: doc.values.bonusYearMostRecent || 0,
                                    yearBefore: doc.values.bonusYearBefore || 0,
                                    yearBeforeThat: doc.values.bonusYearBeforeThat || 0,
                                    guaranteed: doc.values.bonusGuaranteed || false
                                }
                            }
                            if (doc.values.commissionYearMostRecent || doc.values.commissionYearBefore || doc.values.commissionYearBeforeThat) {
                                obj.commission = {
                                    yearMostRecent: doc.values.commissionYearMostRecent || 0,
                                    yearBefore: doc.values.commissionYearBefore || 0,
                                    yearBeforeThat: doc.values.commissionYearBeforeThat || 0,
                                    guaranteed: doc.values.commissionGuaranteed || false
                                }
                            }
                        }

                        const payslips = mortgage.uploadSections.find(s => s.stage === 'APPLY' && s.name === `payslips-${n}` && s.isActive && (legacyEmployment || s.index === emp.id))
                        if (!payslips) {
                            source.addWarning(`No payslips section found for applicant ${n} job ${emp.id || ''}`)
                        } else {
                            // is there a verified other PRA amount here?

                            // haven pension amount
                            const praUpload = payslips.uploads.filter(u => u.isActive && u.isVerifiable && u.values && u.values.pensionDeduction).sort((a, b) => {
                                return b.values.documentDate.diff(a.values.documentDate)
                            })[0]
                            if (praUpload) {
                                const factor = (() => {
                                        switch (emp.payFrequency) {
                                            case Frequencies.WEEKLY:
                                                return 52
                                            case Frequencies.FORTNIGHTLY:
                                                return emp.publicSector ? 26.09 : 26
                                            case Frequencies.MONTHLY:
                                                return 12
                                            case Frequencies.QUARTERLY:
                                                return 4
                                            case Frequencies.YEARLY:
                                                return 1
                                            default:
                                                return 0
                                        }
                                    }
                                )()

                                if (!obj.yearlyPensionContribution) {
                                    obj.yearlyPensionContribution = 0
                                }
                                obj.yearlyPensionContribution += Math.round((praUpload.values.pensionDeduction || 0) * factor * 100) / 100
                                source.methodogy.add(`Pension Contribution ${factor} x ${praUpload.values.pensionDeduction}`, Math.round((praUpload.values.pensionDeduction || 0) * factor * 100) / 100)
                            }
                        }
                    }
                } else {
                    if (legacyEmployment) {
                        if (!emp.grossBasic) {
                            source.addWarning(`Applicant ${n} basic salary not provided for ${emp.currentEmployerName}`)
                        }

                        obj.basic.yearMostRecent = app.income.grossBasic
                        if (app?.income?.incomeChecks?.length) {

                            if (app.income.incomeChecks.includes('OVERTIME')) {
                                obj.overtime = {
                                    yearMostRecent: applicant.income?.overtime,
                                    guaranteed: true
                                }
                            }
                            if (app.income.incomeChecks.includes('BONUS')) {
                                obj.bonus = {
                                    yearMostRecent: applicant.income?.bonus,
                                    guaranteed: true
                                }
                            }
                            if (app.income.incomeChecks.includes('COMMISSION')) {
                                obj.commission = {
                                    yearMostRecent: applicant.income?.commission,
                                    guaranteed: true
                                }
                            }
                            if (app.income.incomeChecks.includes('ALLOWANCES')) {
                                obj.shiftAllowance = {
                                    yearMostRecent: applicant.income?.allowances,
                                    guaranteed: true
                                }
                            }
                            if (app.income.incomeChecks.includes('OTHER')) {
                                obj.otherAllowance = {
                                    yearMostRecent: applicant.income?.other,
                                    guaranteed: true
                                }
                            }
                        }
                    } else {
                        obj.basic.yearMostRecent = emp.grossBasic || 0;
                        if (!emp.grossBasic) {
                            source.addWarning(`Applicant ${n} basic salary not provided for ${emp.currentEmployerName}`)
                        }
                        (emp.incomes || []).filter(it => !!it).forEach(income => {
                            if (income.yearMostRecent || income.amount || income.yearBefore || income.yearBeforeThat) {
                                switch (income.name) {
                                    case 'bonus':

                                        obj.bonus = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }

                                        break
                                    case 'overtime':
                                        obj.overtime = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'commission':
                                        obj.commission = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'shiftAllowance':
                                        obj.shiftAllowance = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'otherAllowance':
                                        obj.otherAllowance = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                    case 'carAllowance':
                                        obj.carAllowance = {
                                            yearMostRecent: income?.yearMostRecent || income?.amount || 0,
                                            yearBefore: income?.yearBefore || 0,
                                            yearBeforeThat: income?.yearBeforeThat || 0,
                                            guaranteed: income.guaranteed
                                        }
                                        break
                                }
                            }
                        })
                    }
                }
                const variables = ['shiftAllowance', 'carAllowance', 'otherAllowance', 'overtime', 'bonus', 'commission']
                obj.hasVariable = variables.some(v => obj[v] && (obj[v].yearMostRecent || obj[v].yearBefore || obj[v].yearBeforeThat))
                const sum = variables.reduce((acc, v) => {

                    if (obj[v]) {
                        acc += obj[v].yearMostRecent || 0
                        acc += obj[v].yearBefore || 0
                        acc += obj[v].yearBeforeThat || 0
                    }
                    return acc
                }, 0)
                obj.hasVariable = sum
                source.setValue(obj)
                return source
            })

        }
        const result = {
            age: age(),
            maritalStatus: maritalStatus(),
            works: works(),
            pra_savings: pra_savings(),
            pra_rent: pra_rent(),
            pra_boarding: pra_boarding(),
            pra_mortgage: pra_mortgage(),
            pra_clearingLoans: pra_clearingLoans(),
            creditLimits: creditFacilityLimits(),
            creditCards: _credit(),
            overdrafts: _overdraft(),
            outgoings_keepingLoans: outgoings_keepingLoans(),
            outgoings_maintenance: outgoings_maintenance(),
            outgoings_childcare: outgoings_childcare(),
            incoming_childrens_allowance: incoming_childrens_allowance(),
            incoming_other_allowance: incoming_other_allowance(),
            lastP60: lastP60(),
            jobs: jobs(),
            additionalData: {
                totalBasicIncome: jobs().reduce((acc, jobSource) => {
                    const job = jobSource.value
                    if (job.basic && job.basic.yearMostRecent) {
                        acc += job.basic.yearMostRecent
                    }
                    return acc
                }, 0),
                totalVariableIncome: jobs().reduce((acc, jobSource) => {
                    const job = jobSource.value
                    if (job.hasVariable) {
                        acc += job.hasVariable
                    }
                    return acc
                }, 0),
                accommodationCosts: [pra_rent(), pra_boarding(), pra_mortgage()].reduce((acc, src) => {
                    if (src && src.value) {
                        acc += src.value
                    }
                    return acc
                }, 0)
            }

        }

        return result
    }
    const htb = () => {
        const source = new figuresSource()
        if (verified) {
            if (mortgage.requirement.helpToBuyScheme !== BoolLike.FALSE) {
                if (mortgage.requirement.helpToBuyScheme !== BoolLike.TRUE) {
                    source.addWarning(`Help to Buy Scheme has not been confirmed`)
                }
                if (!mortgage.requirement.helpToBuySchemeAmount) {
                    source.addWarning(`Help to Buy amount not provided`)
                }
                source.setValue(mortgage.requirement.helpToBuySchemeAmount || 0)
            }
        } else {
            if (mortgage.requirement.helpToBuyScheme !== BoolLike.FALSE) {
                if (mortgage.requirement.helpToBuySchemeAmount == null) {
                    source.setValue(mortgage.twoApplicants ? 30000 : 25000)
                    source.addWarning(`Help to Buy Scheme is estimated`)
                } else {
                    source.setValue(mortgage.requirement.helpToBuySchemeAmount || 0)
                }
            }
        }
        return source
    }
    const retObject = {
        allDependentAges: allDependentAges(),
        fundsAvailable: fundsAvailable(),
        htb: htb(),
        applicant1: applicant(1),
        applicant2: applicant(2),
    }
    retObject.totalAccommodationCosts = [retObject.applicant1?.additionalData?.accommodationCosts || 0, retObject.applicant2?.additionalData?.accommodationCosts || 0].reduce((a, b) => a + b, 0)
    return retObject
}

function mortgageToLenderTestFigures(mortgage, verified = false) {
    const {allDependentAges, htb, fundsAvailable, applicant1, applicant2} = mortgage.lenderTestFigures(verified)
    let warnings = []
    let errors = []
    const addErrorsAndWarnings = (source) => {
        if (!source) return
        if (source.warnings.length) {
            warnings.push(...source.warnings)
        }
        if (source.errors.length) {
            errors.push(...source.errors)
        }
    }
    const figures = {
        "fixedRateYears": 3,
        "mortgageType": mortgage.mortgageType,
        "twoApplicants": mortgage.twoApplicants
    }
    // DEPENDANTS
    const DEPENDANTS = () => {
        const source = allDependentAges
        figures.dependents = source.value
        addErrorsAndWarnings(source)
        // let today = dayjs()
        // figures.dependents = dependents.filter(e=>!!e).map(dep => {
        //
        // 	let dob = dayjs(dep, "YYYY-MM-DD")
        // 	if (!dob.isValid()) {
        // 		warnings.push(`Dependent DOB ${dep} is not a valid date`)
        // 		return {
        // 			age: 0
        // 		}
        // 	}
        // 	let age = today.diff(dob, 'year', true)
        // 	if (age < 0) {
        // 		warnings.push(`Dependent DOB ${dep} is in the future`)
        // 	}
        // 	return {
        // 		age: Math.round(age)
        // 	}
        // })
    }
    const TERM_REQUIRED = () => {
        figures.termRequired = !verified ? mortgage.requirement.loanTerm : mortgage?.activeSubmission?.finalTerm || mortgage?.activeSubmission?.provisionalTerm || mortgage.requirement.loanTerm
    }
    const LOAN_REQUIRED = () => {
        figures.loanRequired = !verified ? mortgage.requirement.loanRequired : mortgage?.activeSubmission?.finalLoan || mortgage?.activeSubmission?.provisionalLoan || mortgage.requirement.loanRequired
    }
    const PROPERTY_VALUE = () => {
        figures.propertyValue = mortgage.property?.valuation2Value ?? mortgage.property?.valuationValue ?? mortgage.property?.purchasePrice ?? mortgage.property?.propertyValue ?? 0
    }
    const APPLICANT = (n) => {
        const app = n === 1 ? applicant1 : applicant2

        let applicant = mortgage[`applicant${n}`]

        // let works = [
        //     EmploymentTypes.EMPLOYED,
        //     EmploymentTypes.SELFEMPLOYED
        // ]
        // let appWorks = legacyEmployment ? works.includes(applicant.employment.employmentType) : !!applicant.employmentIncome.find(income => works.includes(income.employmentType))
        // let dob = dayjs(applicant.personal.dateOfBirth, "YYYY-MM-DD")
        // let today = dayjs()
        // if (!dob.isValid()) {
        //     warnings.push(`Applicant ${n} DOB is not a valid date`)
        //     return {
        //         age: 0
        //     }
        // }
        // let age = today.diff(dob, 'year', true)
        // if (age < 0) {
        //     warnings.push(`Applicant ${n} DOB is in the future`)
        //     return {
        //         age: 0
        //     }
        // }

        // const getJobs = () => {
        //     let jobs = []
        //     if (legacyEmployment) {
        //         jobs.push({
        //             employmentType: applicant.employment.employmentType,
        //             payFrequency: applicant.income.payFrequency
        //         })
        //     } else {
        //         jobs = applicant.employmentIncome
        //     }
        //     return jobs.map(emp => {
        //         let obj = {
        //             sector: emp.employmentType === EmploymentTypes.SELFEMPLOYED ? 'SELF' : emp.publicSector ? 'PUBLIC' : 'PRIVATE',
        //             basic: {
        //                 yearMostRecent: 0,
        //                 guaranteed: true
        //             }
        //         }
        //         if (verified) {
        //             // try salary cert amount
        //             let verifiedSectionSalaryCertificate = mortgage.mortgageUploads.apply.find(s => s.name === `salary-cert-${n}` && (!emp.id || s.index === emp.id))
        //             let certs = (verifiedSectionSalaryCertificate?.verified?.uploads?.active || []).map(verifiedUpload => {
        //                 return verifiedUpload.verified.values
        //             })
        //
        //             if (!certs.length) {
        //                 warnings.push('No salary cert found')
        //             }
        //             let descendingCerts = certs.sort((a, b) => Number(b.date) - Number(a.date))
        //
        //
        //             let activeCert = descendingCerts.length ? descendingCerts[0] : {}
        //
        //             obj.basic.yearMostRecent = activeCert?.grossBasic || 0
        //
        //             if (activeCert.shiftAllowanceYearMostRecent || activeCert.shiftAllowanceYearBefore || activeCert.shiftAllowanceYearBeforeThat || activeCert.allowances) { // legacy){
        //                 obj.shiftAllowance = {
        //                     yearMostRecent: activeCert.shiftAllowanceYearMostRecent || 0,
        //                     yearBefore: activeCert.shiftAllowanceYearBefore || 0,
        //                     yearBeforeThat: activeCert.shiftAllowanceYearBeforeThat || 0,
        //                     guaranteed: activeCert.shiftAllowanceGuaranteed || activeCert.allowancesGuaranteed || false
        //                 }
        //             }
        //             if (activeCert.carAllowanceYearMostRecent || activeCert.carAllowanceYearBefore || activeCert.carAllowanceYearBeforeThat) {
        //                 obj.carAllowance = {
        //                     yearMostRecent: activeCert.carAllowanceYearMostRecent || 0,
        //                     yearBefore: activeCert.carAllowanceYearBefore || 0,
        //                     yearBeforeThat: activeCert.carAllowanceYearBeforeThat || 0,
        //                     guaranteed: activeCert.carAllowanceGuaranteed || false
        //                 }
        //             }
        //             if (activeCert.otherAllowanceYearMostRecent || activeCert.otherAllowanceYearBefore || activeCert.otherAllowanceYearBeforeThat) {
        //                 obj.otherAllowance = {
        //                     yearMostRecent: activeCert.otherAllowanceYearMostRecent || 0,
        //                     yearBefore: activeCert.otherAllowanceYearBefore || 0,
        //                     yearBeforeThat: activeCert.otherAllowanceYearBeforeThat || 0,
        //                     guaranteed: activeCert.otherAllowanceGuaranteed || false
        //                 }
        //             }
        //             if (activeCert.overtimeYearMostRecent || activeCert.overtimeYearBefore || activeCert.overtimeYearBeforeThat || activeCert.overtime) {
        //                 obj.overtime = {
        //                     yearMostRecent: activeCert.overtimeYearMostRecent || activeCert.overtime || 0,
        //                     yearBefore: activeCert.overtimeYearBefore || 0,
        //                     yearBeforeThat: activeCert.overtimeYearBeforeThat || 0,
        //                     guaranteed: activeCert.overtimeGuaranteed || false
        //                 }
        //             }
        //             if (activeCert.bonusYearMostRecent || activeCert.bonusYearBefore || activeCert.bonusYearBeforeThat || activeCert[`${lastYear1}bonus`] || activeCert[`${lastYear2}bonus`] || activeCert[`${lastYear3}bonus`]) {
        //                 obj.bonus = {
        //                     yearMostRecent: activeCert.bonusYearMostRecent || activeCert[`${lastYear1}bonus`] || 0,
        //                     yearBefore: activeCert.bonusYearBefore || activeCert[`${lastYear2}bonus`] || 0,
        //                     yearBeforeThat: activeCert.bonusYearBeforeThat || activeCert[`${lastYear3}bonus`] || 0,
        //                     guaranteed: activeCert.bonusGuaranteed || activeCert[`${lastYear1}bonusGuaranteed`] || false
        //                 }
        //             }
        //             if (activeCert.commissionYearMostRecent || activeCert.commissionYearBefore || activeCert.commissionYearBeforeThat || activeCert.commission) {
        //                 obj.commission = {
        //                     yearMostRecent: activeCert.commissionYearMostRecent || activeCert.commission || 0,
        //                     yearBefore: activeCert.commissionYearBefore || 0,
        //                     yearBeforeThat: activeCert.commissionYearBeforeThat || 0,
        //                     guaranteed: activeCert.commissionGuaranteed || false
        //                 }
        //             }
        //
        //             let verifiedSectionPayslips = mortgage.mortgageUploads.setup.find(s => s.name === `payslips-${n}` && (!emp.id || s.index === emp.id))
        //             let payslips = (verifiedSectionPayslips?.verified?.uploads?.active || []).map(verifiedUpload => {
        //                 return verifiedUpload.verified.values
        //             })
        //             if (!payslips.length) {
        //                 warnings.push('No payslips found')
        //             }
        //
        //             if (!emp.payFrequency) {
        //                 warnings.push(`No pay frequency found for ${emp.currentEmployerName}`)
        //             } else {
        //                 //yearlyAVCContribution
        //                 let avcs = payslips.filter(payslip => !!payslip.avcDeduction).map(payslip => payslip.avcDeduction)
        //                 obj.yearlyAVCContribution = extrapolateYearlyAmount(avcs, emp.payFrequency)
        //
        //                 //yearly Pension
        //                 let pensions = payslips.filter(payslip => !!payslip.pensionDeduction).map(payslip => payslip.pensionDeduction)
        //                 obj.yearlyPensionContribution = extrapolateYearlyAmount(pensions, emp.payFrequency)
        //             }
        //
        //             // basic salary backup try average payslip if no salary cert
        //             if (!obj.basic.yearMostRecent && payslips.length) {
        //
        //                 //let mostRecentPayslip = payslips.reduce((a, b) => a.date > b.date ? a : b, {date: null})
        //                 obj.basic.yearMostRecent = payslips.reduce((acc, payslip) => acc + payslip?.grossBasic || 0, 0) / payslips.length
        //                 //then nultiply by payfreq
        //                 switch (emp.payFrequency) {
        //                     case 'WEEKLY':
        //                         obj.basic.yearMostRecent *= (365 / 7)
        //                         break
        //                     case 'FORTNIGHTLY':
        //                         obj.basic.yearMostRecent *= (365 / 14)
        //                         break
        //                     case 'MONTHLY':
        //                         obj.basic.yearMostRecent *= 12
        //                         break
        //                     case 'QUARTERLY':
        //                         obj.basic.yearMostRecent *= 4
        //                         break
        //                 }
        //             }
        //
        //             // last p60
        //
        //         } else {
        //             if (legacyEmployment) {
        //                 obj.basic.yearMostRecent = applicant.income.grossBasic
        //                 if (applicant.income.incomeChecks?.length) {
        //                     if (applicant.income.incomeChecks.includes('OVERTIME')) {
        //                         obj.overtime = {
        //                             yearMostRecent: applicant.income.overtime,
        //                             guaranteed: true
        //                         }
        //                     }
        //                     if (applicant.income.incomeChecks.includes('BONUS')) {
        //                         obj.bonus = {
        //                             yearMostRecent: applicant.income.bonus,
        //                             guaranteed: true
        //                         }
        //                     }
        //                     if (applicant.income.incomeChecks.includes('COMMISSION')) {
        //                         obj.commission = {
        //                             yearMostRecent: applicant.income.commission,
        //                             guaranteed: true
        //                         }
        //                     }
        //                     if (applicant.income.incomeChecks.includes('ALLOWANCES')) {
        //                         obj.shiftAllowance = {
        //                             yearMostRecent: applicant.income.allowances,
        //                             guaranteed: true
        //                         }
        //                     }
        //                     if (applicant.income.incomeChecks.includes('OTHER')) {
        //                         obj.otherAllowance = {
        //                             yearMostRecent: applicant.income.other,
        //                             guaranteed: true
        //                         }
        //                     }
        //                 }
        //             } else {
        //
        //                 obj.basic.yearMostRecent = emp.grossBasic || 0;
        //                 (emp.incomes || []).forEach(income => {
        //                     switch (income.name) {
        //                         case 'bonus':
        //                             obj.bonus = {
        //                                 yearMostRecent: income?.yearMostRecent || income?.amount || 0,
        //                                 yearBefore: income?.yearBefore || 0,
        //                                 yearBeforeThat: income?.yearBeforeThat || 0,
        //                                 guaranteed: income.guaranteed
        //                             }
        //                             break
        //                         case 'overtime':
        //                             obj.overtime = {
        //                                 yearMostRecent: income?.yearMostRecent || income?.amount || 0,
        //                                 yearBefore: income?.yearBefore || 0,
        //                                 yearBeforeThat: income?.yearBeforeThat || 0,
        //                                 guaranteed: income.guaranteed
        //                             }
        //                             break
        //                         case 'commission':
        //                             obj.commission = {
        //                                 yearMostRecent: income?.yearMostRecent || income?.amount || 0,
        //                                 yearBefore: income?.yearBefore || 0,
        //                                 yearBeforeThat: income?.yearBeforeThat || 0,
        //                                 guaranteed: income.guaranteed
        //                             }
        //                             break
        //                         case 'shiftAllowance':
        //                             obj.shiftAllowance = {
        //                                 yearMostRecent: income?.yearMostRecent || income?.amount || 0,
        //                                 yearBefore: income?.yearBefore || 0,
        //                                 yearBeforeThat: income?.yearBeforeThat || 0,
        //                                 guaranteed: income.guaranteed
        //                             }
        //                             break
        //                         case 'otherAllowance':
        //                             obj.otherAllowance = {
        //                                 yearMostRecent: income?.yearMostRecent || income?.amount || 0,
        //                                 yearBefore: income?.yearBefore || 0,
        //                                 yearBeforeThat: income?.yearBeforeThat || 0,
        //                                 guaranteed: income.guaranteed
        //                             }
        //                             break
        //                         case 'carAllowance':
        //                             obj.carAllowance = {
        //                                 yearMostRecent: income?.yearMostRecent || income?.amount || 0,
        //                                 yearBefore: income?.yearBefore || 0,
        //                                 yearBeforeThat: income?.yearBeforeThat || 0,
        //                                 guaranteed: income.guaranteed
        //                             }
        //                             break
        //                     }
        //                 })
        //
        //             }
        //         }
        //         return obj
        //     })
        // }
        // const getMonthlyOutgoings = () => {
        //
        //     const loans = applicant.debts.filter(d => d.debtType === DebtTypes.LOAN && !d.clearedBeforeMortgage && !d.isClosed).reduce((acc, debt) => acc + debt.monthlyPayment, 0)
        //
        //     const maintenance = applicant.financial.paysMaintenance ? applicant.financial.maintenanceCosts : 0
        //     const childcare = applicant.personal.countDependents > 0 ? applicant.financial.monthlyChildcareCosts : 0
        //     return {
        //         loans,
        //         maintenance,
        //         childcare
        //     }
        // }
        // const getCreditFacilityLimits = () => {
        //     const credits = applicant.debts.filter(debt => [
        //         DebtTypes.CREDITCARD,
        //         DebtTypes.STORECARD,
        //         DebtTypes.OVERDRAFT
        //     ].includes(debt.debtType) && !debt.isClosed)
        //     return credits.reduce((acc, debt) => {
        //         acc += debt.creditLimit
        //         return acc
        //     }, 0)
        // }
        // const getPRA = () => {
        //     return {
        //         mortgage: app.pra_mortgage?.value,
        //         rent: app.pra_rent?.value,
        //         boarding: app.pra_boarding?.value,
        //         savings: app.pra_savings?.value,
        //         clearingLoans: app.pra_clearingLoans?.value
        //     }
        //     // Accom Costs
        //     // if (mortgage.mortgageType !== MortgageTypes.PURCHASE || mortgage.applicationType !== ApplicationTypes.FIRSTTIME) {
        //     //     pra.mortgage = app.pra_mortgage
        //     // } else {
        //     //     if (applicant.personal.accommodationSituation === PersonalAccommodationSituations.RENTED) {
        //     //         pra.rent = applicant.financial.monthlyAccommodationCosts || 0
        //     //     } else if (applicant.personal.accommodationSituation === PersonalAccommodationSituations.RELATIVES && applicant.financial.contributeToRelatives) {
        //     //         pra.boarding = applicant.financial.monthlyAccommodationCosts || 0
        //     //     }
        //     // }
        //     //
        //     // // Savings
        //     // if (verified) {
        //     //     pra.savings = applicant.verifiedAccounts['€']?.averageMonthlySavings || 0
        //     // } else {
        //     //     let filter = asset => asset.assetType === AssetTypes.SAVING && [
        //     //         Frequencies.MONTHLY,
        //     //         Frequencies.WEEKLY,
        //     //         Frequencies.FORTNIGHTLY,
        //     //     ].includes(asset.increasesFrequency)
        //     //     let allSavings = applicant.assets.filter(filter)
        //     //
        //     //     pra.savings = allSavings.reduce((acc, asset) => {
        //     //         switch (asset.increasesFrequency) {
        //     //             case Frequencies.MONTHLY:
        //     //                 acc += asset.increases
        //     //                 break
        //     //             case Frequencies.FORTNIGHTLY:
        //     //                 acc += Number((asset.increases * 26 / 12).toFixed(2))
        //     //                 break
        //     //             default:
        //     //                 acc += Number((asset.increases * 52 / 12).toFixed(2))
        //     //         }
        //     //         return acc
        //     //     }, 0)
        //     //     if (applicant.financial.hasCurrentAccountSavings) {
        //     //         pra.savings += applicant.financial.monthlySavings
        //     //     }
        //     //
        //     // }
        //     //
        //     // //Clearing Loans1
        //     // pra.clearingLoans = applicant.debts.filter(d => d.debtType === DebtTypes.LOAN && !!d.clearedBeforeMortgage && !d.isClosed).reduce((acc, debt) => acc + (debt.monthlyPayment || 0), 0)
        //     // return pra
        //
        // }
        // const getLastP60 = () => {
        //     if (verified) {
        //         let verifiedEDS = mortgage.mortgageUploads.setup.find(s => s.name === `employment-summary-${n}`)
        //         let certs = (verifiedEDS?.verified?.uploads?.active || []).map(verifiedUpload => {
        //             return verifiedUpload.verified.values
        //         })
        //         if (!certs.length) {
        //             warnings.push('No EDS found')
        //             return 0
        //         }
        //         // most recent based on year
        //         let activeCert = certs.sort((a, b) => Number(b.year) - Number(a.year))[0]
        //         return Number(activeCert.grossBasic || 0) + Number(activeCert.grossBasic1 || 0) + Number(activeCert.grossBasic2 || 0)
        //     }
        //     return 0
        // }
        const getPortfolio = () => {
            function calculateNewMortgagePayment(currentPayment, oldRate, newRate, termYears) {
                const months = termYears * 12;

                // Convert annual interest rates to monthly decimal rates
                const oldMonthlyRate = oldRate / 100 / 12;
                const newMonthlyRate = newRate / 100 / 12;

                // Reverse calculate loan principal (P) using the current payment
                const principal = currentPayment * (Math.pow(1 + oldMonthlyRate, months) - 1) / (oldMonthlyRate * Math.pow(1 + oldMonthlyRate, months));

                // Calculate the new monthly payment with the new interest rate
                const newPayment = principal * (newMonthlyRate * Math.pow(1 + newMonthlyRate, months)) / (Math.pow(1 + newMonthlyRate, months) - 1);

                return newPayment;
            }

            return (applicant.properties || []).filter(p => !!p.isMortgaged).map(p => {
                return {
                    isMortgaged: true,
                    outstandingMortgage: p.outstanding || 0,
                    rate: p.rate,
                    rateFixedYears: p.rateFixedYears,
                    tracker: false,
                    interestOnly: p.interestOnly,
                    remainingTermInMonths: p.remainingTermInMonths,
                    yearlyRentalIncome: p.rentPerAnnum,
                    propertyValue: p.estimatedValue,
                    foreign: p.foreign,
                    nonStandard: false,
                    numberOfBedrooms: p.bedrooms,
                    annualStressedMortgagePayment: calculateNewMortgagePayment(p.monthlyRepayments, p.rate, p.rate + 2, p.remainingTermInMonths / 12),
                }
            })
        }

        addErrorsAndWarnings(app.works)
        addErrorsAndWarnings(app.age)
        addErrorsAndWarnings(app.pra_mortgage)
        addErrorsAndWarnings(app.pra_rent)
        addErrorsAndWarnings(app.pra_boarding)
        addErrorsAndWarnings(app.pra_savings)
        addErrorsAndWarnings(app.pra_clearingLoans)
        addErrorsAndWarnings(app.lastP60)
        addErrorsAndWarnings(app.outgoings_keepingLoans)
        addErrorsAndWarnings(app.outgoings_maintenance)
        addErrorsAndWarnings(app.outgoings_childcare)
        addErrorsAndWarnings(app.creditLimits)

        figures[`applicant${n}`] = {
            works: app.works.value,
            maritalStatus: applicant.personal.maritalStatus,
            age: app.age.value,
            jobs: app.jobs.map(job => {
                addErrorsAndWarnings(job)
                return job.value

            }),
            pra: {
                mortgage: app.pra_mortgage?.value,
                rent: app.pra_rent?.value,
                boarding: app.pra_boarding?.value,
                savings: app.pra_savings?.value,
                clearingLoans: app.pra_clearingLoans?.value
            },
            lastP60: app.lastP60?.value,
            monthlyOutgoings: {
                loans: app.outgoings_keepingLoans?.value,
                maintenance: app.outgoings_maintenance?.value,
                childcare: app.outgoings_childcare?.value
            },
            monthlyIncomes: {
                childrensAllowance: app.incoming_childrens_allowance?.value,
                otherAllowance: app.incoming_other_allowance?.value,
            },
            creditFacilityLimits: app.creditLimits?.value,
            credit:{
                overdraftLimits: app.overdrafts?.latestOverdraftLimits?.value,
                creditCardLimits: app.creditCards?.latestCreditLimits?.value,
                overdraftsUtilization: app.overdrafts?.highestOverdraftUtilization?.value,
                creditCardsUtilization: app.creditCards?.highestCreditUtilization?.value,
            },
            portfolio: getPortfolio()
        }
    }

    // ITEMS THAT DONT EXIST YET - TODO: CREATE ON FRONTEND
    // ## Accommodation COsts should be for everyone ( ie, their contribution)
    const EXISTING = () => {
        if (mortgage.mortgageType === MortgageTypes.PURCHASE && [
            ApplicationTypes.MOVINGHOME,
            ApplicationTypes.SECONDTIME
        ].includes(mortgage.applicationType)) {
            figures.existing = {
                propertyValue: mortgage?.existing?.propertyValue || 0,
                outstandingMortgage: mortgage?.existing?.outstanding || 0,
                remainingTermInMonths: mortgage?.existing?.remainingTermInMonths || 0,
                retaining: mortgage?.existing?.retaining || 0,
                rate: mortgage?.existing?.currentRate || 0,
                rateFixedYears: mortgage?.existing?.rateFixedYears || 0,
                numberOfBedrooms: mortgage?.existing?.numberOfBedrooms || 0
            }
        }
    }
    const DEPOSIT = () => { // movers only - i dont think this even does anything yet
        if (mortgage.mortgageType === MortgageTypes.PURCHASE && [
            ApplicationTypes.MOVINGHOME,
            ApplicationTypes.SECONDTIME
        ].includes(mortgage.applicationType)) {
            figures.deposit = mortgage.requirement.depositSavings
        }
    }
    const ADULTS_BOARDING = () => { // This is really adult dependents
        if (mortgage.property?.adultsBoarding) {
            figures.numberOfAdultsBoarding = 0 //mortgage.property?.adultsBoardingCount
        }
    }
    const SELF_BUILD = () => {
        figures.selfBuild = false
        if (mortgage.property?.buildType === BuildType.SELFBUILD) {
            figures.selfBuild = {
                costToBuild: mortgage.property?.constructionCosts,
                siteValue: mortgage.property?.siteValue,
            }
        }
    }
    const NEW_BUILD = () => {
        figures.newBuild = mortgage.property?.buildType === BuildType.NEW
    }
    const ONE_BED_PROPERTY = () => {
        figures.oneBedProperty = mortgage.property?.numberOfBedrooms === 1
    }
    const PROPERTY_IDENTIFIED = () => {
        figures.propertyIdentified = mortgage.property?.addressKnown
    }
    const BER_SCORE = () => {
        figures.berScore = mortgage.property?.berScore
    }
    const PROPERTY_TYPE = () => {
        figures.propertyType = mortgage.property?.propertyType
    }
    const APPLICATION_TYPE = () => {
        figures.applicationType = mortgage.applicationType
    }
    const FUNDS_AVAILABLE = () => {
        const source = fundsAvailable
        figures.fundsAvailable = source.getValue()
        if (source.errors.length) {
            errors.push(...source.errors)
        }
        if (source.warnings.length) {
            warnings.push(...source.warnings)
        }
        // let funds = mortgage.requirement.receivingGifts ? mortgage.requirement.depositGifts || 0 : 0
        // funds += mortgage.requirement.depositSavings || 0
        // figures.fundsAvailable = funds
    }

    function NUMBER_IN_HOUSEHOLD() {
        figures.numberInHousehold = 1 + (figures.dependents ? figures.dependents.length : 0)
        if (mortgage.twoApplicants) {
            figures.numberInHousehold += 1
        }
    }

    const HTB = () => {
        const source = htb
        figures.htb = source?.value
        if (source.errors.length) {
            errors.push(...source.errors)
        }
        if (source.warnings.length) {
            warnings.push(...source.warnings)
        }
    }
    DEPENDANTS()
    TERM_REQUIRED()
    LOAN_REQUIRED()
    PROPERTY_VALUE()
    APPLICANT(1)
    if (mortgage.twoApplicants) {
        APPLICANT(2)
    }
    EXISTING()
    DEPOSIT()
    ADULTS_BOARDING()
    SELF_BUILD()
    ONE_BED_PROPERTY()
    PROPERTY_IDENTIFIED()
    BER_SCORE()
    PROPERTY_TYPE()
    APPLICATION_TYPE()
    FUNDS_AVAILABLE()
    NUMBER_IN_HOUSEHOLD()
    NEW_BUILD()
    HTB()
    return {
        errors,
        warnings,
        figures,
    }
}

export default mortgageToLenderTestFigures;